import React, { ChangeEvent, useEffect } from 'react';
import { useState } from 'react';
import { Button, Divider, Grid, useMediaQuery, Link, CssBaseline, IconButton } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import WhiteInosio from '../../../icons/whiteinosio.svg';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { selectAuthState, getPasswordLogin, getRefreshUser, switchProfileRequestReceived } from '../../../shared/sagas/auth/authSlice';
import { RightContainer } from '../../../components/RightContainer';
import { CreateApplicationV2Dto, UpsertApplicantV2Dto } from '@inosio/payments-sdk';
import { EmailRegex } from '../../../utils/regex';
import InputControllerLogin from '../../../components/Form/InputControllerLogin';
import { ApplicantPageStates } from '../Applicant';
import BackIcon from '../../../icons/back1.svg';
import { selectApplicationsState } from '../sagas/pm/applicationsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { createApplicantV2, createApplicationV2, selectApplicantsState } from '../sagas/applicants/applicantsSlice';

interface State {
  email: string;
  password: string;
  showPassword: boolean;
}

interface ApplicantLoginProps {
  setPageState: (pageState: ApplicantPageStates) => void;
  query: URLSearchParams;
}

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  rightContainer: {
    background: '#053742',
    width: '59%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  leftContainer: {
    borderRadius: '24px 0px 0px 24px',
    background: '#ffffff',
    width: '42%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  brandContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
  },
  brandTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '36px',
  },
  brandText: {
    fontFamily: 'BMWHelvetica',
    fontWeight: 700,
    fontSize: '24px',
  },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    width: '340px',
    border: 'none',
  },
  buttonContainer: {
    width: '340px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'space-between',
    justifyContent: 'space-between',
    marginTop: '20px',
    paddingBottom: '20px',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
  },
  btnStyle: {
    font: '14px Work Sans 400 !important',
    width: '113px',
    height: '49px',
    borderRadius: '24px !important',
    textTransform: 'capitalize',
    backgroundColor: '#39A2DB',
    color: '#FFFFFF !important',
  },
  btnStyleMobileScreen: {
    font: '14px Work Sans !important',
    width: '100%',
    height: '49px',
    borderRadius: '24px !important',
    textTransform: 'capitalize',
  },
  newAccountText: {
    color: 'rgba(211, 211, 211, 1)',
    marginLeft: '25px',
    fontSize: '16px',
    marginBottom: '5px',
  },
  isActive: {
    background: 'rgba(57, 162, 219, 1) !important',
    boxShadow: ' 0px 0px 40px rgba(0, 0, 0, 0.1) !important',
    color: '#fff !important',
  },
  isNotActive: {
    background: 'rgba(211, 211, 211, 1) !important',
    boxShadow: ' 0px 0px 40px rgba(0, 0, 0, 0.1) !important',
  },
  brandContainerMobileScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'start',
    marginBottom: '30px',
  },
  brandTextMobileScreen: {
    fontSize: '20px !important',
  },
  buttonContainerMobileScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    // marginTop: "170px",
    paddingBottom: '20px',
  },
  signUpBtn: {
    font: '14px Work Sans !important',
    border: '1px solid #39A2DB !important',
    width: '113px',
    height: '49px',
    borderRadius: '24px !important',
  },
  forgetText: {
    font: '14px Work Sans',
    lineHeight: '30px',
    color: '#39A2DB',
    textDecoration: 'none !important',
    maxHeight: '30px !important',
    margin: '10px !important',
    marginTop: '8px !important',
    marginBottom: '27px !important',
    paddingLeft: '5px !important',
    cursor: 'pointer',
    zIndex: 30,
  },
}));

const theme = createTheme({
  typography: {
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 16,
  },
});

enum applicantTypeFlow {
  PrimaryApplicant,
  Coapplicant,
}

enum registrationStep {
  NotYetLoggedIn, //means we must login first
  LoggedInInitially, //means the user has logged in, but not necessarily on the correct profile. either create or switch to AO profile
  IsOnApplicantProfile, //we are on the correct profile. If primary, create application, else skip to next step
  ApplicationIsReady, // when application is ready, nativate the user to that page
}

/**
 * The purpose of this page is to allow pre-existing users to login and start an app (vs creating a net new user).
 * I'm not crazy that it's a separate page than the main applicant registration, but it is what it is.
 * @param props
 * @returns
 */
export default function ApplicantLogin(props: ApplicantLoginProps) {
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matches900 = useMediaQuery('(max-width:900px)');
  const matches1200 = useMediaQuery('(max-width:1200px)');
  const classes = useStyles();
  const navigation = useHistory();
  const dispatch = useDispatch();

  //selectors
  const { isFetching, isSuccess: isAuthSuccess, isError, ipaUser } = useSelector(selectAuthState);
  const { invitedApplicationResponse, propertyDetails } = useSelector(selectApplicationsState);
  const { isCreateApplicantSuccess, Applicant, isCreateApplicationSuccess, Application } = useSelector(selectApplicantsState);
  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);

  const [values, setValues] = React.useState<State>({
    email: invitedApplicationResponse?.Coapplicant?.Email ?? '',
    password: '',
    showPassword: false,
  });
  const [emailError, setEmailError] = useState('');
  const [passError, setPassError] = useState('');
  const [globalError, setGlobalError] = useState('');

  const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const class1 = !matchesMobile ? classes.btnStyle : classes.btnStyleMobileScreen;
  const class2 = classes.isActive;

  //get all of our optional coapplicant flow parameters from the query
  const applicationUuid = props.query.get('application');
  const coapplicantUuid = props.query.get('coapplicant');
  const hmac = props.query.get('hmac');
  const [regStep, setRegStep] = useState<registrationStep>(registrationStep.NotYetLoggedIn);

  //* because switching profiles and relogging resets the entire app state, we have to save the local stuff in a local state. That's what the below is doing
  const [applicantUuid, setApplicantUuid] = useState('');
  const [propertyInfo] = useState<{
    propertyAddress: string;
    locationUuid: string | null | undefined;
  }>(propertyDetails);
  const [organizationUuid] = useState(propertyGroupDetails.Uuid);
  const applicantType = coapplicantUuid ? applicantTypeFlow.Coapplicant : applicantTypeFlow.PrimaryApplicant;
  /*
  Steps for primary applicant:
  1. Login, if success, go to next step
  2. Make call to create applicant record
    a. if success, move to next step
    b. if error, display
  3. Relog the user so it gets a new token with the applicant as the currentPrincipal
    a. if success, move to next step  
  4. Make call to create application
    a. if success, go to next steps
  5. Navigate user to application page


  steps for coapplicant
  - same exact as above, just skip step 2
  - invocation of create applicant has slightly diff params

  */

  const fireOffCreatePrimaryApplicantReq = () => {
    dispatch({
      type: createApplicantV2.type,
      payload: {
        applicantDto: {
          FirstName: '',
          LastName: '',
          UserUuid: ipaUser!.userUuid,
          Email: ipaUser?.username,
        } as UpsertApplicantV2Dto,
      },
    });
  };

  const fireOffCreateCoApplicantReq = () => {
    dispatch({
      type: createApplicantV2.type,
      payload: {
        applicantDto: {
          FirstName: '',
          LastName: '',
          Email: ipaUser?.username,
          ApplicationUuid: applicationUuid,
          Hmac: hmac,
          Uuid: coapplicantUuid,
        } as UpsertApplicantV2Dto,
      },
    });
  };

  const fireOffCreateApplicationReq = () => {
    dispatch({
      type: createApplicationV2.type,
      payload: {
        PrimaryApplicantOccupantUuid: ipaUser?.currentProfile.uuid,
        PropertyAddress: propertyInfo.propertyAddress,
        LocationUuid: propertyInfo.locationUuid || null,
        OrganizationUuid: organizationUuid,
      } as CreateApplicationV2Dto,
    });
  };

  const validateEmail = (inputedEmail: string) => {
    if (!inputedEmail) {
      setEmailError(() => 'Email address is empty!');
      return false;
    } else if (!EmailRegex.test(inputedEmail.trim())) {
      setEmailError(() => 'Invalid email address!');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (inputtedPassword: string) => {
    if (inputtedPassword.length === 0) {
      setPassError('Password is empty!');
      return false;
    } else {
      setPassError('');
      return true;
    }
  };

  //step 1. login
  const handleSubmit = (event: any) => {
    event?.preventDefault();

    if (validateEmail(values.email) && validatePassword(values.password)) {
      dispatch({
        type: getPasswordLogin.type,
        payload: {
          email: values.email.trim(),
          password: values.password,
        },
      });
    }
  };

  //step 2, if login success && we have't yet created the AO record, so create applicant record
  useEffect(() => {
    if (
      isAuthSuccess && // login is success
      ipaUser?.userUuid && // there's a useruuid
      regStep === registrationStep.NotYetLoggedIn //means he must have just logged in now
    ) {
      setRegStep(registrationStep.LoggedInInitially);
      if (applicantType === applicantTypeFlow.Coapplicant) {
        fireOffCreateCoApplicantReq();
      } else {
        fireOffCreatePrimaryApplicantReq();
      }
    }
  }, [dispatch, ipaUser, isAuthSuccess, regStep]);

  useEffect(() => {
    if (isCreateApplicantSuccess && Applicant.Uuid) {
      setApplicantUuid(Applicant.Uuid);
    }
  }, [isCreateApplicantSuccess, Applicant]);

  //step 3, relog once coapplicant creation is success
  useEffect(() => {
    if (
      regStep === registrationStep.LoggedInInitially &&
      applicantUuid && //
      ipaUser?.currentProfile.uuid //
    ) {
      if (ipaUser.currentProfile.uuid === applicantUuid) {
        // user is on new applicant profile
        setRegStep(registrationStep.IsOnApplicantProfile);
      } else if (ipaUser.profiles.find((p) => p.uuid === applicantUuid)) {
        //new applicant profile exists in his profile list so just switch profile
        dispatch({
          type: switchProfileRequestReceived.type,
          payload: {
            principalUuid: applicantUuid,
          },
        });
      } else {
        //!Forgot when this flow happens
        //relog
        dispatch({
          type: getPasswordLogin.type,
          payload: {
            email: values.email.trim(),
            password: values.password,
          },
        });
      }
    }
  }, [dispatch, applicantUuid, ipaUser, regStep]);

  //step 4, create application if primary flow
  useEffect(() => {
    if (applicantType === applicantTypeFlow.PrimaryApplicant && regStep === registrationStep.IsOnApplicantProfile) {
      //i must be already re-logged, so just create a new application
      fireOffCreateApplicationReq();
    }
  }, [dispatch, ipaUser, Applicant, regStep]);

  //step 4.5
  useEffect(() => {
    if (regStep === registrationStep.IsOnApplicantProfile && applicantType === applicantTypeFlow.PrimaryApplicant && isCreateApplicationSuccess) {
      setRegStep(registrationStep.ApplicationIsReady);
    } else if (applicantType === applicantTypeFlow.Coapplicant && regStep === registrationStep.IsOnApplicantProfile) {
      //if it's coapplicant flow, we do it once the relog is success
      setRegStep(registrationStep.ApplicationIsReady);
    }
  }, [dispatch, Application, isCreateApplicationSuccess, regStep]);

  //step 5, Navigate user to application page.
  useEffect(() => {
    if (regStep === registrationStep.ApplicationIsReady) {
      const _applicationUuid = applicantType === applicantTypeFlow.PrimaryApplicant ? Application.Uuid : applicationUuid;

      if (_applicationUuid) {
        navigation.push(`/resui/applications/${_applicationUuid}`);
      }
    }
  }, [navigation, regStep]);

  useEffect(() => {
    if (isError) setGlobalError('The email or password is invalid. Please try again');
  }, [isError]);

  const handleGoBack = () => {
    props.setPageState(ApplicantPageStates.Landing);
    dispatch(getRefreshUser());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.rootContainer}>
        <CssBaseline />
        <div className={classes.leftContainer}>
          {globalError.length ? (
            <div className={classes.globalError}>
              <Typography
                style={{
                  fontFamily: 'BMWHelvetica',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
                data-e2e="globalError"
              >
                {' '}
                {globalError}
              </Typography>
            </div>
          ) : (
            ''
          )}
          <div className={`${!matchesMobile ? classes.brandContainer : classes.brandContainerMobileScreen}`}>
            <Box pb={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: 340 }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="go to prev"
                onClick={handleGoBack}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '-8px',
                }}
              >
                <img src={BackIcon} alt="back" style={{ cursor: 'pointer' }} />
              </IconButton>
              <img src={WhiteInosio} style={{ width: '150px', height: '150px' }} alt="logo" />
            </Box>
          </div>
          <Box component="form" onSubmit={handleSubmit} noValidate className={classes.formContainer}>
            <div>
              <InputControllerLogin
                name={'email'}
                label={'Email'}
                onChange={handleChange('email')}
                value={values.email}
                autoComplete="email"
                isError={!!emailError.length}
                errors={emailError}
                isfullWidth={true}
              />
            </div>
            <div style={{ display: 'flex' }}>
              <InputControllerLogin
                name={'password '}
                label={'Password'}
                onChange={handleChange('password')}
                required={true}
                autoComplete="password"
                isError={!!passError.length}
                errors={passError}
                value={values.password}
                isfullWidth={true}
                type="password"
              />
            </div>
            <div className={`${!matchesMobile ? classes.buttonContainer : classes.buttonContainerMobileScreen}`}>
              {!matchesMobile ? (
                <div className={classes.wrapper}>
                  <Button onClick={() => props.setPageState(ApplicantPageStates.Registration)} className={classes.signUpBtn}>
                    Sign Up
                  </Button>
                </div>
              ) : (
                <span className={classes.newAccountText}>
                  {' '}
                  Don't have an account yet?&nbsp;
                  <Link style={{ color: '#39A2DB', textDecoration: 'none', cursor: 'pointer' }} onClick={() => props.setPageState(ApplicantPageStates.Registration)}>
                    SignUp
                  </Link>{' '}
                </span>
              )}
              <div className={classes.wrapper}>
                <Button disabled={isFetching} type="submit" className={`${class1} ${class2}`} id="signInButton">
                  Sign in
                </Button>
                {isFetching ? (
                  <CircularProgress
                    size={26}
                    sx={{
                      color: '#39A2DB',
                    }}
                    className={classes.buttonProgress}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
            <Divider style={{ width: '100%' }} />
            <Grid item sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
              <Link href="http://support.inosio.com/" className={classes.forgetText}>
                Need Help?
              </Link>
              <Grid item sx={{ height: '30px', marginTop: '8px' }}>
                <Divider orientation="vertical" flexItem style={{ height: '100%' }} />
              </Grid>
              <Link component={RouterLink} to="/forgetpassword" className={classes.forgetText}>
                Forgot Password?
              </Link>
            </Grid>
          </Box>
        </div>
        {!(matches900 && matches1200) ? <RightContainer /> : <></>}
      </div>
    </ThemeProvider>
  );
}
